/**
 * Client-side draft checks while editing a note (quotes, required fields).
 */
import type { Questionnaire, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { AiNoteCheckResult, NoteCheckFinding } from '@/pages/appointments/fhirNoteMapping';
import { CHECK_LABELS } from '@/pages/appointments/noteCheckConstants';
import { extractDirectQuoteSnippets } from '@/pages/appointments/utils/directQuotesDetection';
import {
  formatFindingsPreview,
  formatNoteCheckFindings,
} from '@/pages/appointments/utils/noteCheckFormatting';
import { collectQuestionnaireAnswerFields } from '@/pages/appointments/utils/questionnaireAnswerFields';
import { getMissingRequiredFields } from '@/pages/appointments/utils/questionnaireResponseRequired';

export { detectDirectQuotesInText } from '@/pages/appointments/utils/directQuotesDetection';

function buildRequiredFieldFindings(
  questionnaire: Questionnaire,
  responseItems: QuestionnaireResponseItem[] | undefined,
): NoteCheckFinding[] {
  const draftResponse = {
    resourceType: 'QuestionnaireResponse' as const,
    status: 'in-progress' as const,
    item: responseItems,
  };
  return getMissingRequiredFields(questionnaire, draftResponse).map((field) => ({
    location: field.label,
    message: 'This required question is empty.',
    itemPath: field.itemPath,
  }));
}

function buildQuoteFindings(
  questionnaire: Questionnaire,
  responseItems: QuestionnaireResponseItem[] | undefined,
): NoteCheckFinding[] {
  const draftResponse = {
    resourceType: 'QuestionnaireResponse' as const,
    status: 'in-progress' as const,
    item: responseItems,
  };
  const findings: NoteCheckFinding[] = [];
  for (const field of collectQuestionnaireAnswerFields(questionnaire, draftResponse)) {
    const snippets = extractDirectQuoteSnippets(field.text);
    for (const snippet of snippets) {
      findings.push({
        location: field.label,
        message: `Direct quote detected: "${snippet}". Consider paraphrasing.`,
        itemPath: field.itemPath,
      });
    }
  }
  return findings;
}

export function buildLocalDraftChecks(input: {
  questionnaire: Questionnaire;
  responseItems: QuestionnaireResponseItem[] | undefined;
}): AiNoteCheckResult[] {
  const requiredFindings = buildRequiredFieldFindings(input.questionnaire, input.responseItems);
  if (requiredFindings.length > 0) {
    return [
      {
        id: 'required-fields',
        label: CHECK_LABELS['required-fields'],
        status: 'flagged',
        findings: requiredFindings,
        detail: formatFindingsPreview(requiredFindings) ?? formatNoteCheckFindings(requiredFindings),
      },
      ...buildQuoteCheck(input),
    ];
  }

  return [
    {
      id: 'required-fields',
      label: CHECK_LABELS['required-fields'],
      status: 'passed',
    },
    ...buildQuoteCheck(input),
  ];
}

function buildQuoteCheck(input: {
  questionnaire: Questionnaire;
  responseItems: QuestionnaireResponseItem[] | undefined;
}): AiNoteCheckResult[] {
  const quoteFindings = buildQuoteFindings(input.questionnaire, input.responseItems);
  if (quoteFindings.length > 0) {
    return [
      {
        id: 'quotes-in-note',
        label: CHECK_LABELS['quotes-in-note'],
        status: 'flagged',
        findings: quoteFindings,
        detail: formatNoteCheckFindings(quoteFindings),
      },
    ];
  }

  return [
    {
      id: 'quotes-in-note',
      label: CHECK_LABELS['quotes-in-note'],
      status: 'passed',
    },
  ];
}

/** Merge server checks with local draft checks (local wins for overlapping draft-only ids). */
export function mergeDraftAndServerChecks(
  server: AiNoteCheckResult[] | undefined,
  local: AiNoteCheckResult[],
): AiNoteCheckResult[] {
  const byId = new Map<string, AiNoteCheckResult>();
  for (const c of server ?? []) byId.set(c.id, c);
  for (const c of local) {
    if (c.id === 'quotes-in-note' || c.id === 'required-fields') {
      byId.set(c.id, c);
    }
  }
  return [...byId.values()];
}
